import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Globe2, Mountain, Waves, Tag, ArrowUpDown } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const B = `${API}/api/files/dersimizcografiya/uploads`;
const LOCAL_HERO = `${B}/7b1de8e6-cf40-4f99-be57-48941bc4ef9a.jpg`;

const defaultImages = [
  `${B}/47ef7813-7734-4f04-bad2-41faed93bdbd.jpg`,
  `${B}/6c1e8512-f1b6-40d6-8b84-3c9e14379c1a.jpg`,
  `${B}/8323a863-2eaa-456c-8e55-ee3c07bfd141.jpg`,
  `${B}/ef2756c6-5b2c-4b74-a57a-2a8e9f5b7a0c.jpg`,
  `${B}/af46362f-3e81-4e09-89e3-5da894bd82b9.jpg`,
  `${B}/f959a2d9-7cfe-459c-b0bf-42d8f29bae91.jpg`,
];

const catLabels = { yenilik: 'Yenilik', ders: 'Dərs Materialı', imtahan: 'İmtahan', tedris: 'Tədris', digər: 'Digər' };
const catColors = { yenilik: 'bg-[#0077B6] text-white', ders: 'bg-emerald-600 text-white', imtahan: 'bg-amber-500 text-white', tedris: 'bg-purple-600 text-white', digər: 'bg-slate-600 text-white' };

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
}

export default function News() {
  const [news, setNews] = useState([]);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/news`),
      axios.get(`${API}/api/site-content`)
    ]).then(([newsRes, contentRes]) => {
      setNews(newsRes.data);
      setContent(contentRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  const pg = content.news_page || {};
  const heroBadge = pg.hero_badge || 'Son Yeniliklər';
  const heroTitle = pg.hero_title || 'Yeniliklər';
  const heroSubtitle = pg.hero_subtitle || 'Coğrafiya təhsili sahəsində son yeniliklər, dərs materialları və tədbirlər';
  const heroBg = pg.hero_bg || LOCAL_HERO;
  const emptyTitle = pg.empty_title || 'Hələ ki yenilik yoxdur';
  const emptySubtitle = pg.empty_subtitle || 'Yeni məlumatlar tezliklə əlavə olunacaq';

  return (
    <div>
      {/* Hero */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#03045E]/40 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-bold mb-6 border border-white/20">
              <Globe2 className="w-4 h-4" /> {heroBadge}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">{heroTitle}</h1>
            <p className="text-lg md:text-xl text-white font-semibold max-w-2xl mx-auto">{heroSubtitle}</p>
          </motion.div>
        </div>
        <motion.div className="absolute bottom-12 left-12 hidden lg:block" animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity }}><Mountain className="w-10 h-10 text-white/15" /></motion.div>
        <motion.div className="absolute top-24 right-16 hidden lg:block" animate={{ y: [0, 8, 0] }} transition={{ duration: 5, repeat: Infinity }}><Waves className="w-8 h-8 text-white/10" /></motion.div>
      </section>

      {/* News Grid */}
      <section className="py-16 md:py-24 bg-[#F4F7F6]" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Sort Bar */}
          {news.length > 0 && (
            <div className="flex items-center justify-between mb-8">
              <p className="text-[#5C6B7A] text-sm">{news.length} yenilik</p>
              <div className="flex items-center gap-2">
                <button onClick={() => {
                  const reverse = { newest: 'oldest', oldest: 'newest', az: 'za', za: 'az' };
                  setSortBy(reverse[sortBy] || 'oldest');
                }} className="w-8 h-8 rounded-lg bg-white hover:bg-[#E2E8F0] flex items-center justify-center transition-colors" title="Əks sıralama">
                  <ArrowUpDown className="w-4 h-4 text-[#5C6B7A]" />
                </button>
                {[
                  { id: 'newest', label: 'Yeni → Köhnə' },
                  { id: 'oldest', label: 'Köhnə → Yeni' },
                  { id: 'az', label: 'A → Z' },
                  { id: 'za', label: 'Z → A' },
                ].map(s => (
                  <button key={s.id} onClick={() => setSortBy(s.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${sortBy === s.id ? 'bg-[#0077B6] text-white' : 'bg-white text-[#5C6B7A] hover:bg-[#E2E8F0]'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {news.length === 0 ? (
            <div className="text-center py-20">
              <Globe2 className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <p className="text-lg text-[#5C6B7A]">{emptyTitle}</p>
              <p className="text-sm text-[#5C6B7A]/60 mt-2">{emptySubtitle}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...news].sort((a, b) => {
                const dateA = new Date(a.published_date || a.created_at);
                const dateB = new Date(b.published_date || b.created_at);
                if (sortBy === 'newest') return dateB - dateA;
                if (sortBy === 'oldest') return dateA - dateB;
                if (sortBy === 'az') return a.title.localeCompare(b.title, 'az');
                if (sortBy === 'za') return b.title.localeCompare(a.title, 'az');
                return 0;
              }).map((item, i) => {
                const img = item.image_url || defaultImages[i % defaultImages.length];
                return (
                  <motion.article key={item.id} className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 flex flex-col"
                    initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    data-testid={`news-card-${item.id}`}>
                    <Link to={`/news/${item.id}`} className="flex flex-col flex-1">
                    <div className="relative h-56 overflow-hidden">
                      <img src={img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                        <Calendar className="w-3.5 h-3.5 text-[#0077B6]" />
                        <span className="text-xs font-semibold text-[#1A2530]">{formatDate(item.published_date || item.created_at)}</span>
                      </div>
                      {item.category && (
                        <div className="absolute top-4 right-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-md ${catColors[item.category] || catColors.digər}`}>
                            <Tag className="w-3 h-3" /> {catLabels[item.category] || item.category}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-xl font-bold text-[#1A2530] mb-3 line-clamp-2 min-h-[3.5rem] group-hover:text-[#0077B6] transition-colors">{item.title}</h3>
                      <p className="text-sm text-[#5C6B7A] leading-relaxed mb-4 line-clamp-3 min-h-[3.75rem]">{item.summary || ''}</p>
                      <div className="mt-auto">
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0077B6] group-hover:gap-3 transition-all">
                          Ətraflı oxu <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                    </Link>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
